import { ComponentType } from "react";

// Map frontend IDs to backend card_type values
export const CARD_TYPE_MAP: Record<string, string> = {
  aiPromptRole: "role",
  aiPromptSiteContext: "site_context",
  aiPromptConditionalInstructions: "conditional_instructions",
  aiPromptRiskTolerance: "risk_and_tolerance",
  aiPromptIncentiveIntent: "incentive_intent",
  aiPromptConstraints: "constraints",
  aiPromptQualityCheck: "quality_check",
  aiPromptTradeAppendices: "trade_appendices"
};

// Import detail components
import CategoryDetail from "../detailComponents/CategoryDetail";
import TagDetail from "../detailComponents/TagDetail";
import StatusDetail from "../detailComponents/StatusDetail";
import TypeDetail from "../detailComponents/TypeDetail";
import PhaseDetail from "../detailComponents/PhaseDetail";
import TeamDetail from "../detailComponents/TeamDetail";
import IconDetail from "../detailComponents/IconDetail";
import IntegrationDetail from "../detailComponents/IntegrationDetail";
import PermissionDetail from "../detailComponents/PermissionDetail";
import AIPromptDetail from "../detailComponents/AIPromptDetail";
import NotificationDetail from "../detailComponents/NotificationDetail";
import ScheduleDetail from "../detailComponents/ScheduleDetail";
import BoardSettingsDetail from "../detailComponents/BoardSettingsDetail";
import OpenAIDetail from "../detailComponents/OpenAIDetail";
import CompanyCamDetail from "../detailComponents/CompanyCamDetail";

export interface SubcategoryDetailProps {
  subcategory: Subcategory;
  categoryId?: string;
}

export interface Subcategory {
  id: string;
  label: string;
  component: ComponentType<SubcategoryDetailProps>;
  required?: boolean; // Show "Required" badge, enforce min 1 card
}

export interface SettingsCategory {
  id: string;
  label: string;
  icon: string;
  subcategories: Subcategory[];
}

export const SETTINGS_STRUCTURE: Record<string, SettingsCategory> = {
  personal: {
    id: "personal",
    label: "Personal",
    icon: "fa-regular fa-user",
    subcategories: [
      {
        id: "notifications",
        label: "Notifications",
        component: NotificationDetail
      },
      {
        id: "personalSchedule",
        label: "Personal Schedule Settings",
        component: ScheduleDetail
      },
      {
        id: "personalBoard",
        label: "Personal Board Settings",
        component: BoardSettingsDetail
      },
      {
        id: "access",
        label: "Can-View & Can-Edit Defaults",
        component: PermissionDetail
      }
    ]
  },

  general: {
    id: "general",
    label: "General",
    icon: "fa-regular fa-gear",
    subcategories: [
      {
        id: "generalBoard",
        label: "General Board Settings",
        component: BoardSettingsDetail
      },
      {
        id: "swivuAssist",
        label: "Swivu Assist",
        component: IntegrationDetail
      },
      {
        id: "icons",
        label: "Icons",
        component: IconDetail
      },
      {
        id: "companyCam",
        label: "Company Cam Token",
        component: IntegrationDetail
      }
    ]
  },

  schedule: {
    id: "schedule",
    label: "Schedule",
    icon: "fa-regular fa-calendar",
    subcategories: [
      {
        id: "scheduleSettings",
        label: "Schedule Settings",
        component: ScheduleDetail
      }
    ]
  },

  tasks: {
    id: "tasks",
    label: "Tasks",
    icon: "fa-regular fa-list-check",
    subcategories: [
      {
        id: "taskGeneral",
        label: "General Task Settings",
        component: BoardSettingsDetail
      },
      {
        id: "taskAutoArchive",
        label: "Auto Archive",
        component: BoardSettingsDetail
      },
      {
        id: "taskCategories",
        label: "Categories",
        component: CategoryDetail
      },
      {
        id: "taskTags",
        label: "Tags",
        component: TagDetail
      },
      {
        id: "taskStatuses",
        label: "Statuses",
        component: StatusDetail
      },
      {
        id: "taskPhase",
        label: "Phase",
        component: PhaseDetail
      }
    ]
  },

  resources: {
    id: "resources",
    label: "Resources",
    icon: "fa-regular fa-users",
    subcategories: [
      {
        id: "resourceTypes",
        label: "Types",
        component: TypeDetail
      },
      {
        id: "resourceCategories",
        label: "Categories",
        component: CategoryDetail
      },
      {
        id: "resourceTags",
        label: "Tags",
        component: TagDetail
      },
      {
        id: "resourceStatuses",
        label: "Statuses",
        component: StatusDetail
      }
    ]
  },

  clients: {
    id: "clients",
    label: "Clients",
    icon: "fa-regular fa-building",
    subcategories: [
      {
        id: "clientCategories",
        label: "Categories",
        component: CategoryDetail
      },
      {
        id: "clientTags",
        label: "Tags",
        component: TagDetail
      },
      {
        id: "clientStatuses",
        label: "Statuses",
        component: StatusDetail
      }
    ]
  },

  contacts: {
    id: "contacts",
    label: "Contacts",
    icon: "fa-regular fa-address-book",
    subcategories: [
      {
        id: "contactCategories",
        label: "Categories",
        component: CategoryDetail
      },
      {
        id: "contactTags",
        label: "Tags",
        component: TagDetail
      },
      {
        id: "contactStatuses",
        label: "Statuses",
        component: StatusDetail
      }
    ]
  },

  teams: {
    id: "teams",
    label: "Teams",
    icon: "fa-regular fa-people-group",
    subcategories: [
      {
        id: "teamsGeneral",
        label: "General",
        component: TeamDetail
      }
    ]
  },

  budget: {
    id: "budget",
    label: "Budget",
    icon: "fa-regular fa-dollar-sign",
    subcategories: [
      {
        id: "budgetSettings",
        label: "Budget Settings",
        component: BoardSettingsDetail
      }
    ]
  },

  incentives: {
    id: "incentives",
    label: "Incentives",
    icon: "fa-regular fa-trophy",
    subcategories: [
      {
        id: "incentivePermissions",
        label: "Permissions",
        component: PermissionDetail
      },
      {
        id: "incentiveCategoriesAndTags",
        label: "Categories & Tags",
        component: CategoryDetail
      }
    ]
  },

  integrations: {
    id: "integrations",
    label: "Integrations",
    icon: "fa-regular fa-plug",
    subcategories: [
      {
        id: "openai",
        label: "OpenAI",
        component: OpenAIDetail
      },
      {
        id: "companyCam",
        label: "Company Cam",
        component: CompanyCamDetail
      }
    ]
  },

  aiPrompt: {
    id: "aiPrompt",
    label: "AI Prompt",
    icon: "fa-regular fa-sparkles",
    subcategories: [
      {
        id: "aiPromptRole",
        label: "Role",
        component: AIPromptDetail,
        required: true
      },
      {
        id: "aiPromptSiteContext",
        label: "Site Context",
        component: AIPromptDetail,
        required: true
      },
      {
        id: "aiPromptConditionalInstructions",
        label: "Conditional Instructions",
        component: AIPromptDetail,
        required: true
      },
      {
        id: "aiPromptRiskTolerance",
        label: "Risk and Tolerance",
        component: AIPromptDetail,
        required: false
      },
      {
        id: "aiPromptIncentiveIntent",
        label: "Incentive Intent",
        component: AIPromptDetail,
        required: false
      },
      {
        id: "aiPromptConstraints",
        label: "Constraints",
        component: AIPromptDetail,
        required: true
      },
      {
        id: "aiPromptQualityCheck",
        label: "Quality Check",
        component: AIPromptDetail,
        required: false
      },
      {
        id: "aiPromptTradeAppendices",
        label: "Trade Appendices",
        component: AIPromptDetail,
        required: false
      }
    ]
  }
};

// Helper functions
export const getCategories = (): SettingsCategory[] => {
  return Object.values(SETTINGS_STRUCTURE);
};

export const getCategory = (categoryId: string): SettingsCategory | null => {
  return SETTINGS_STRUCTURE[categoryId] || null;
};
